// Custom React hook for Application Insights telemetry
import { useEffect, useCallback } from 'react';
import { 
  trackEvent, 
  trackException, 
  trackTrace, 
  trackMetric, 
  trackDependency,
  setUserContext,
  clearUserContext
} from '../config/appInsights';

export const useAppInsights = () => {
  // Track page view when component mounts
  const trackPageView = useCallback((name, url = window.location.href, properties = {}) => {
    trackEvent('PageView', {
      pageName: name,
      url: url,
      ...properties
    });
  }, []);

  // Track custom events
  const trackCustomEvent = useCallback((eventName, properties = {}, measurements = {}) => {
    trackEvent(eventName, properties, measurements);
  }, []);

  // Track errors with context
  const trackError = useCallback((error, context = {}) => {
    trackException(error, {
      component: context.component || 'Unknown',
      operation: context.operation || 'Unknown',
      ...context
    });
  }, []);

  // Track performance metrics
  const trackPerformance = useCallback((metricName, value, properties = {}) => {
    trackMetric(metricName, value, {
      timestamp: new Date().toISOString(),
      ...properties
    });
  }, []);

  // Track API calls
  const trackApiCall = useCallback((apiName, url, duration, success, statusCode, properties = {}) => {
    trackDependency('HTTP', apiName, url, duration, success, {
      statusCode: statusCode,
      ...properties
    });
  }, []);

  // Track user interactions
  const trackUserInteraction = useCallback((interaction, element, properties = {}) => {
    trackEvent('UserInteraction', {
      interactionType: interaction,
      element: element,
      timestamp: new Date().toISOString(),
      ...properties
    });
  }, []);

  // Track business events
  const trackBusinessEvent = useCallback((eventType, properties = {}) => {
    trackEvent('BusinessEvent', {
      eventType: eventType,
      timestamp: new Date().toISOString(),
      ...properties
    });
  }, []);

  // Set authenticated user
  const setUser = useCallback((userId, email, additionalProperties = {}) => {
    setUserContext(userId, null, {
      email: email,
      ...additionalProperties
    });
  }, []);

  // Clear user on logout
  const clearUser = useCallback(() => {
    clearUserContext();
  }, []);

  return {
    trackPageView,
    trackCustomEvent,
    trackError,
    trackPerformance,
    trackApiCall,
    trackUserInteraction,
    trackBusinessEvent,
    setUser,
    clearUser
  };
};

// HOC for automatic page view tracking
export const withAppInsights = (WrappedComponent, pageName) => {
  return function AppInsightsWrapper(props) {
    const { trackPageView } = useAppInsights();
    
    useEffect(() => {
      trackPageView(pageName || WrappedComponent.displayName || WrappedComponent.name);
    }, [trackPageView]);
    
    return <WrappedComponent {...props} />;
  };
};

// Hook for tracking component lifecycle
export const useComponentTracking = (componentName) => {
  const { trackCustomEvent } = useAppInsights();
  
  useEffect(() => {
    // Track component mount
    trackCustomEvent('ComponentMount', {
      componentName: componentName,
      timestamp: new Date().toISOString()
    });
    
    // Track component unmount
    return () => {
      trackCustomEvent('ComponentUnmount', {
        componentName: componentName,
        timestamp: new Date().toISOString()
      });
    };
  }, [componentName, trackCustomEvent]);
};